/* eslint no-lonely-if: "off" */

// accountForAggregation
// addIconsToContentType
// additionalLegendText
// alignGraphDataTypeByPerspective
// areChildrenDifferentlyActive
// arrayHasObject
// arraysMatchByKey
// arraySum
// calcSidebarWidth
// clampOverflowsById
// cloneIdName
// containsArray
// countKeysInArray
// countryLookup
// createProposedNarrative
// describeFilters
// determineSliceInDays
// emailHasExtension
// findCommonRange
// findTourElementPosition
// findUsableBaseTrend
// formatFilters
// formatNewTrend
// formatTagsForInfoTooltip
// getScrollbarSize
// groupNarratives
// highest
// installColorSchemeAndFormatTags
// isUserSubscribed
// log
// lowest
// makeFetchKey
// mapToArray
// orFilterGroup
// removeObjectFromArr
// removeStringFromArr
// resetPerspective
// setMapKey
// setStartDate
// tagNameLookup
// totalArticlesOnThisDate
// usePrevious
// writeDateRangeString

import React, { useRef, useEffect } from "react";
import _ from "underscore";
import moment from "moment";
import dateLib from "lib/dateLib.js";
import { abbreviatedFilterText, addOrRemove, getDateObjectFromUTC, removeMd5FromStart } from "@exo/helpers";
import { addColorToNestedArray, assignStaticColor, colors } from "lib/color.js";
import { adjustStartDateForAggregation } from "lib/helpers/graphHelpers.js";
import { removeChildren, isAncestorActive, proxyDeselection } from "lib/helpers/nestedListHelpers.js";

import {
	Data,
	DotPyramid,
	File,
	Image,
	LineChart,
	LineChartDashed,
	MapPin,
	Microphone,
	Newspaper,
	NewspaperStack,
	Quote,
	Star,
	Synopsis,
	Umbrella,
	User,
	Users,
} from "@exo/icons";
import { width } from "window-size";

// HACK: work around a very weird issue where fetch.js can't import from lib/colors.js directly
export { assignStaticColor, colors };

const getCookie = (name) => {
	return document.cookie.split(";").some((c) => {
		return c.trim().startsWith(`${name  }=`);
	});
};
export const deleteCookie = (name, path, domain) => {
	if (getCookie(name)) {
		document.cookie =
			`${name  }=${  path ? `;path=${  path}` : ""  }${domain ? `;domain=${  domain}` : ""  };expires=Thu, 01 Jan 1970 00:00:01 GMT`;
	}
};

export const resetGoogleTranslate = () => {
	const iframe = document.getElementsByClassName("skiptranslate")[1];
	if (!iframe) return;	
	const innerDoc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
	if (innerDoc) {
		// fire `click` event on the `restore` button, that `Shows the origin`
		const restoreEl = innerDoc.getElementsByTagName("button");
		for (let i = 0; i < restoreEl.length; i++) {
			if (restoreEl[i].id.indexOf("restore") >= 0) {
				restoreEl[i].click();
			}
		}
		// fire `click` event on the `close` button
		// 	const closeLink = innerDoc.querySelector('[title="Close"]');
		// 	if (closeLink) {
		// 		closeLink.click();
		// 	}
	}
};

// This is different than @exo/icons > pickIcon because it passes a function that returns the component, instead of the component
//    otherwise, the ids and icons should match
const addIconsToContentType = (params) => {
	const { array } = params;

	const clonedArray = array ? JSON.parse(JSON.stringify(array)) : [];

	// ------ id index - for CBP Analysis Source --------
	// Chart  - 1d36783e12317ed400ebeddeb072a27b
	// Dataset - 239658e016e3d5d06ae719d280a79fec
	// Document - 0945359809dad1fbf3dea1c95a0da951
	// Event - a4ecfc70574394990cf17bd83df499f7
	// Event Prediction - 1782e50fa12a993f7f375801425b04ac
	// Forecast - 89c1265be62d3ba835a3d963db5956b0
	// Image - be53a0541a6d36f6ecb879fa2c584b08
	// Multiple Articles - f8cd85705829d9bf9cabcb36421b0094
	// Other - 6311ae17c1ee52b36e68aaf4ad066387
	// Single Article - a188b63325f862df8fb2e0a0452f5eda
	// Social Media Post - c84ea71b6da11034c6dfa3710c57b3ee
	// Social Media Thread/Group of Posts - 74ecdfd16dd669e83d390d55ab5e0a0c

	if (clonedArray.children) {
		_.each(clonedArray.children, (filter) => {
			if (filter.id === "article" || filter.id === "a188b63325f862df8fb2e0a0452f5eda") {
				filter.icon = (fill) => <Newspaper fill={fill} />;
			} else if (filter.id === "broadcast") {
				filter.icon = (fill) => <Microphone fill={fill} />;
			} else if (filter.id === "Event" || filter.id === "event" || filter.id === "a4ecfc70574394990cf17bd83df499f7") {
				filter.icon = (fill) => <MapPin fill={fill} scale={1.2} />;
			} else if (filter.id === "analysis" || filter.id === "summary" || filter.id === "739e6d2a73723ec7b1919fa5a51f9b07") {
				filter.icon = (fill) => <Synopsis fill={fill} scale={1.1} />;
			} else if (filter.id === "quote") {
				filter.icon = (fill) => <Quote fill={fill} />;
			} else if (filter.id === "chart" || filter.id === "1d36783e12317ed400ebeddeb072a27b") {
				filter.icon = (fill) => <LineChart fill={fill} />;
			} else if (filter.id === "social" || filter.id === "c84ea71b6da11034c6dfa3710c57b3ee") {
				filter.icon = (fill) => <User fill={fill} />;
			} else if (filter.id === "74ecdfd16dd669e83d390d55ab5e0a0c") {
				filter.icon = (fill) => <Users fill={fill} />;
			} else if (filter.id === "be53a0541a6d36f6ecb879fa2c584b08") {
				filter.icon = (fill) => <Image fill={fill} />;
			} else if (filter.id === "239658e016e3d5d06ae719d280a79fec") {
				filter.icon = (fill) => <Data fill={fill} />;
			} else if (filter.id === "89c1265be62d3ba835a3d963db5956b0") {
				filter.icon = (fill) => <Umbrella fill={fill} scale={1.1} />;
			} else if (filter.id === "f8cd85705829d9bf9cabcb36421b0094") {
				filter.icon = (fill) => <NewspaperStack fill={fill} scale={1.1} />;
			} else if (filter.id === "0945359809dad1fbf3dea1c95a0da951") {
				filter.icon = (fill) => <File fill={fill} />;
			} else if (filter.id === "6311ae17c1ee52b36e68aaf4ad066387") {
				filter.icon = (fill) => <DotPyramid fill={fill} />;
			} else if (filter.id === "1782e50fa12a993f7f375801425b04ac") {
				filter.icon = (fill) => <LineChartDashed fill={fill} />;
			} else if (filter.id === "featured") {
				filter.icon = (fill) => <Star fill={fill} />;
			}
		});
	}

	return clonedArray;
};

export const additionalLegendText = (params) => {
	// mapHighlightTitle: string
	// filtersDisplayText: string of comma separated strings: "filter1, filter2, filter3"
	// filtersList: Array of objects [{ id: string, name: string, parent: string, active: bool, type(optional): string}]
	// mapConfig: object, but just checked for truthiness
	const { mapHighlightTitle, filtersDisplayText, filtersList, mapConfig } = params;

	// console.log("mapHighlightTitle", mapHighlightTitle);
	// console.log("filtersDisplayText", filtersDisplayText);
	// console.log("filtersList", filtersList);
	// console.log("mapConfig", mapConfig);

	const checkedDisplayText = filtersDisplayText || "";

	const hasLocationAlready = _.find(filtersList, (filter) => {
		return filter.parent === "sourceState" || filter.parent === "audienceState";
	});

	// Remove redundant filter text
	let filtersDisplayArray = checkedDisplayText.split(/\s*,\s*/);
	filtersDisplayArray = _.reject(filtersDisplayArray, (filterText) => {
		return filterText === mapHighlightTitle;
	});
	const filtersDisplayTextClean = filtersDisplayArray.join(", ");

	const mapString = `${
		hasLocationAlready || mapHighlightTitle ? mapHighlightTitle || filtersDisplayTextClean : mapConfig ? "National" : ""
	}`;
	const remainderString = `${
		filtersDisplayTextClean && filtersDisplayTextClean !== mapHighlightTitle && (!hasLocationAlready || mapHighlightTitle)
			? `${filtersDisplayTextClean}`
			: ""
	}`;
	const colonOrNot = mapString && remainderString ? ": " : "";
	// console.log(`${mapString}${colonOrNot}${remainderString}`);

	return `${mapString}${colonOrNot}${remainderString}`;
};

// QUOKKA.js tests for additionalLegendText
// const sampleFiltersList = [
// 	{ id: "filter1_id", name: "filter1", parent: "filter1_parent", type: "Persons" },
// 	{ id: "filter2_id", name: "filter2", parent: "filter2_parent", active: true, type: "Persons" },
// 	{ id: "filter3_id", name: "filter3", parent: "filter3_parent", active: true, type: "Persons" },
// ];
// const singleFiltersList = [
// 	{ id: "filter1_id", name: "filter1", parent: "sourceState", type: "Persons" },
// ];

// additionalLegendText("", "filter1, filter2, filter3", sampleFiltersList, { garge: false }); // "National: filter1, filter2, filter3"
// additionalLegendText("filter1", "filter1, filter2, filter3", sampleFiltersList, { garge: false }); // "filter1: filter2, filter3"
// additionalLegendText("", "filter1", singleFiltersList, { garge: false }); // "filter1"
// additionalLegendText("filter1", "filter1, filter2, filter3", sampleFiltersList, false); // "filter1, filter2, filter3"
// additionalLegendText("", "filter1, filter2, filter3", sampleFiltersList, false); // "filter1, filter2, filter3"

// Align the graph data type with the perspective. This means the perspective overrides the local graph view
// FLAG_REMOVE_PERSPECTIVE_TOGGLE
export const alignGraphDataTypeByPerspective = (params) => {
	const { perspective, graphDataTypeActive, graphDataTypes } = params;

	if (!perspective) {
		return graphDataTypeActive;
	} else if (!graphDataTypes) {
		// No switcher supported, so it should match perspective
		return perspective === "Source"
			? { name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" }
			: { name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" };
	} else {
		const dataByPerspective = _.find(graphDataTypes, (obj) => {
			return obj.perspectiveOverride === perspective;
		});
		return dataByPerspective || graphDataTypeActive;
	}
};

// ------- areChildrenDifferentlyActive --------
// Returns true if children arrays' active is different, else false
// [{
//   objectName: {
//     children: [
//       {
//         childName: {
//           active: boolean
//         }
//       }
//     ]
//   }
// }]
export const areChildrenDifferentlyActive = (array1, array2) => {
	let childrenAreDifferentlyActive = false;

	_.find(array1, (array1Slice) => {
		// find so we can break out if true
		// break this once it's true
		if (childrenAreDifferentlyActive) {
			return;
		}

		// find the matching parent
		const array2Slice = _.find(array2, (f) => {
			return f.id === array1Slice.id;
		});

		// iterate over the children
		_.find(array1Slice.children, (oneChild) => {
			if (childrenAreDifferentlyActive) {
				return;
			}
			const twoChild = _.find(array2Slice.children, (f2) => {
				return f2.id === oneChild.id;
			});
			if (oneChild.active !== twoChild.active) {
				childrenAreDifferentlyActive = true;
			}
		});
	});

	return childrenAreDifferentlyActive;
};

// Returns true if the array has a an object with a matching key:value pair
//  key: 'string'
export const arrayHasObject = (array, object, key) => {
	// check by comparing each value
	const theyMatch = _.find(array, (arrayObject) => {
		return arrayObject[key] === object[key];
	});

	return theyMatch && true;
};

// Returns true if each array has a an object with a matching key.
// If the optional key2 is specified a match will only succedd if 
// either neither of the compared array alements matching on key
// contain a property key2 or if they both contain the same key2 value
// NOTE: expects both arrays to have unique values for key
export const arraysMatchByKey = (array1, array2, key, key2=null) => {
	let arraysMatch = true;

	// quick check by length
	if (array1.length !== array2.length) {
		arraysMatch = false;
	} else {
		// check by comparing each value
		_.find(array1, (match1) => {
			const theyMatch = _.find(array2, (match2) => {
				let match = Boolean(match1[key] && match2[key] && match1[key] === match2[key]);
				if (key2 && match) {
					match = Boolean((!match1[key2] && !match2[key2]) || (match1[key2] === match2[key2]));
				}
				return Boolean(match);
			});
			if (theyMatch === undefined) {
				arraysMatch = false;
			}
		});
	}

	return Boolean(arraysMatch);
};

export const arraySum = (array) => {
	return array.reduce((a, b) => a + b, 0);
};

// figures out the sidebar width, based on the current window size
//	a simple helper to keep things from getting messy
export const calcSidebarWidth = (params) => {
	const { window } = params;
	const percentage = window * 0.22;
	if (percentage > 300) {
		return 300;
	} else if (percentage < 280) {
		return 280;
	} else {
		return percentage;
	}
};

// Is the clamped element getting cropped?
// id: String - must be a valid id in the DOM to work
export const clampOverflowsById = (id) => {
	const element = document.getElementById(id);
	return element && element.scrollHeight && element.clientHeight && element.scrollHeight > element.clientHeight;
};

// Clone an array of objects with 'id' and 'name' as only keys for each object
export const cloneIdName = (original) => {
	const clone = [];
	_.each(original, (o) => {
		const x = {
			id: o.id,
			name: o.name,
		};
		clone.push(x);
	});
	return clone;
};

// Returns true if the array contains any of the values from valuesArray
//		Expects two arrays of strings or numbers
export const containsArray = (array, valuesArray) => {
	// every returns true if every iteration is true
	const hasNone = _.every(valuesArray, (value) => {
		return !_.contains(array, value);
	});
	return !hasNone; // true if it contains a value
};

// Count the keys in a given array of objects.
export const countKeysInArray = (params) => {
	const { array } = params;

	if (!_.isArray(array)) {
		return "countKeysInArray requires an array to count anything";
	} else {
		const objKeys = {};

		_.each(array, (obj) => {
			const keys = Object.keys(obj);
			_.each(keys, (k) => {
				const doesThisCount = !_.isNull(obj[k]) && !_.isUndefined(obj[k] && (_.isString(obj[k]) ? obj[k] !== "" : true)) ? 1 : 0;
				objKeys[k] = objKeys[k] ? objKeys[k] + doesThisCount : doesThisCount;
			});
		});

		return objKeys;
	}
};

// Count the occurances of each string in a given array of strings.
export const countStringsInArray = (params) => {
	const { array } = params;

	if (!_.isArray(array)) {
		return "countStringsInArray requires an array to count anything";
	} else {
		const stringCount = {};

		_.each(array, (str) => {
			if (stringCount[str]) {
				stringCount[str]++;
			} else {
				stringCount[str] = 1;
			}
		});

		return stringCount;
	}
};

const countryObjects = [
	{ long: "Estonia", short: "EE" },
	{ long: "Latvia", short: "LV" },
	{ long: "Lithuania", short: "LT" },
	{ long: "Moldova", short: "MD" },
	{ long: "Russia", short: "RU" },
	{ long: "China", short: "CN" },
	{ long: "Australia", short: "AU" },
];
// returns the short code for a country
export const countryLookup = (longName) => {
	const matchedCountry = _.find(countryObjects, (country) => {
		return country.long === longName;
	});
	return matchedCountry ? matchedCountry.short : longName;
};

export const createProposedNarrative = (params) => {
	const { allowedWidth, flatNarratives, proposedNarrative, narrative } = params;
	let updatedNarrative = proposedNarrative && JSON.parse(JSON.stringify(proposedNarrative));

	const removeNarrative =
		updatedNarrative &&
		_.find(updatedNarrative.compositeNarratives, (existing) => {
			return existing.id === narrative.id;
		});

	// Remove the narrative if it was already in proposedNarrative.compositeNarratives
	if (removeNarrative) {
		// proposedNarrative should be false if there was only one narrative
		if (updatedNarrative.compositeNarratives.length === 1 && updatedNarrative.compositeNarratives[0].id === narrative.id) {
			updatedNarrative = false;
		} else {
			const withoutNarrative = _.reject(updatedNarrative.compositeNarratives, (existing) => {
				return existing.id === narrative.id;
			});

			// If there's more than one narrative remaining, update composite narratives and other data
			if (withoutNarrative.length > 1) {
				updatedNarrative.compositeNarratives = withoutNarrative;
				updatedNarrative.name = abbreviatedFilterText({
					filtersList: withoutNarrative,
					allOR: true,
					availableSpace: allowedWidth * 2 - 80, // Take a little bit off to allow for awkward line breaks
				});
				updatedNarrative.id = _.pluck(withoutNarrative, "id").join(",");

				// Set data for single narrative
				updatedNarrative.info = `This is a composite of ${_.pluck(withoutNarrative, "name").join(" OR ")}`;
				updatedNarrative.parent = "Custom";
			} else {
				// Otherwise we're back to a normal single narrative
				updatedNarrative = JSON.parse(JSON.stringify(withoutNarrative[0]));
				updatedNarrative.compositeNarratives = withoutNarrative;
			}
		}
	} else {
		const compositeNarratives = updatedNarrative && updatedNarrative.compositeNarratives ? updatedNarrative.compositeNarratives : [];

		// ---- Logic for nested selections ----
		// If there is an active anscestor, the selection will not be active, but needs to be deselected
		const activeAncestor = isAncestorActive({
			familyTree: flatNarratives,
			active: compositeNarratives,
			selected: narrative,
		});

		// Toggle current selection, or deselect make a proxyDeselection if there is an active ancestor
		const updatedSelected = activeAncestor
			? proxyDeselection({
					array: compositeNarratives,
					activeAncestor,
					familyTree: flatNarratives,
					deselect: narrative,
					childName: "children",
			  })
			: addOrRemove(compositeNarratives, narrative);

		// Remove any children of the selected option - they will appear active but not get passed to the API
		const withNarrative = removeChildren({
			array: updatedSelected,
			parent: narrative,
			childName: "children",
		});

		// building a new object because updatedNarrative can be false
		updatedNarrative = {
			name: abbreviatedFilterText({
				filtersList: withNarrative,
				allOR: true,
				availableSpace: allowedWidth * 2 - 80, // Take a little bit off to allow for awkward line breaks
			}),
			id: _.pluck(withNarrative, "id").join(","),
			parent: withNarrative.length > 1 ? "Custom" : narrative.parent,
			info: withNarrative.length > 1 ? `This is a composite of ${_.pluck(withNarrative, "name").join(", ")}` : narrative.info,
			weight: updatedNarrative ? updatedNarrative.weight : narrative.weight,
			color: updatedNarrative ? updatedNarrative.color : narrative.color,
			compositeNarratives: withNarrative,
			children: narrative.children, // will only be used if one topic is select
		};
	}

	return updatedNarrative;
};

export const noPrefixAbbreviatedFilterText = ({filtersList,	defaultText, availableSpace}) => {
	const updatedFiltersList = filtersList.map((filter) => {
		const thisIsLocation = (filter.parent === "sourceState") || (filter.parent === "sourceProvince");
		const prefixRemovedFilterName = thisIsLocation 
			? filter.name.substring(filter.name.indexOf(":") + 1) 
			: filter.name;
		const trimmedFilterName = prefixRemovedFilterName.trim();
		return {...filter, name: trimmedFilterName};
	});
	return abbreviatedFilterText({filtersList: updatedFiltersList,	defaultText, availableSpace});
}

// Creates an AND/OR string for the filters.
// NOTE: Returns false or altText if filtersList is false or []
export const describeFilters = (params) => {
	const { filtersList, altText, keywordsAND = true } = params;

	if (!filtersList || filtersList.length <= 0) {
		return altText !== undefined ? altText : false;
	} else {
		let filtersDisplayText = "";
		const filterTypeGroups = _.groupBy(filtersList, "parent"); // returns an object

		_.each(filterTypeGroups, (filters) => {
			const theseAreKeywords = filters[0].parent === "keywords";
			const theseAreLocations = (filters[0].parent === "sourceState") || (filters[0].parent === "sourceProvince");

			// Make an array of the filter names
			const groupFilterNamesArr = filters.map((filter) => {
				const prefixRemovedFilterName = theseAreLocations 
					? filter.name.toString().substring(filter.name.indexOf(":") + 1) 
					: filter.name.toString();
				const trimmedFilterName = prefixRemovedFilterName ? prefixRemovedFilterName.trim() : "INVALID";
				return removeMd5FromStart({ string: trimmedFilterName });
			});

			if (theseAreKeywords && keywordsAND) {
				// convert the filter names into a string
				const andString = groupFilterNamesArr.join(" AND ");

				// Add the group to the main string
				// no AND on the first loop
				filtersDisplayText = filtersDisplayText ? `${filtersDisplayText} AND ${andString}` : andString;
			} else {
				// convert the filter names into a string
				const filtersString = groupFilterNamesArr.join(" OR ");
				const filtersInParens = groupFilterNamesArr && groupFilterNamesArr.length > 1 ? `(${filtersString})` : filtersString;

				// Add the group to the main string
				// no AND on the first loop
				filtersDisplayText = filtersDisplayText ? `${filtersDisplayText} AND ${filtersInParens}` : filtersInParens;
			}
		});

		// Groups locations ONLY by (filter1 OR filter2)
		// let filtersDisplayText = "";
		// const splitFilters = _.partition(filtersList, (filter) => {
		// 	return filter.parent === "audienceState" || filter.parent === "sourceState";
		// });
		// const nonLocationFilterTypeGroups = _.groupBy(splitFilters[1], "parent"); // returns an object

		// _.each(nonLocationFilterTypeGroups, (filters) => {
		// 	// Make an array of the filter names
		// 	const groupFilterNamesArr = filters.map((filter) => {
		// 		const trimmedFilterName = filter.name.trim();
		// 		return removeMd5FromStart({ string: trimmedFilterName });
		// 	});
		// 	// convert the filter names into a string
		// 	const namesString = groupFilterNamesArr.join(" AND ");

		// 	// Add the group to the main string
		// 	// no AND on the first loop
		// 	filtersDisplayText = filtersDisplayText ? `${filtersDisplayText} AND ${namesString}` : namesString;
		// });

		// if (splitFilters[0] && splitFilters[0].length > 0) {
		// 	const locationNamesArr = splitFilters[0].map((locationFilter) => {
		// 		const trimmedFilterName = locationFilter.name.trim();
		// 		return removeMd5FromStart({ string: trimmedFilterName });
		// 	});

		// 	const locationNamesString = locationNamesArr.join(" OR ");
		// 	const locationsInParens = locationNamesArr && locationNamesArr.length > 1 ? `(${locationNamesString})` : locationNamesString;

		// 	// Add the group to the main string
		// 	// no AND on the first loop
		// 	filtersDisplayText = filtersDisplayText ? `${filtersDisplayText} AND ${locationsInParens}` : locationsInParens;
		// }

		return filtersDisplayText;
	}
};

// Returns a number
//		segmentSpan: { name: string, id: number }
//		startDate: utc date string
//		endDate: utc date string
export const determineSliceInDays = (params) => {
	const { segmentSpan, startDate, endDate } = params;
	const defaultSlice = 7;
	let slice = false;

	if (!segmentSpan || !segmentSpan.id) {
		console.error("determineSliceInDays requires segmentSpan.id");
	} else if (!startDate) {
		console.error("determineSliceInDays requires startDate");
	} else if (!endDate) {
		console.error("determineSliceInDays requires endDate");
	} else {
		const daysInRange = moment.duration(Number(endDate) - Number(startDate)).asDays();

		// divide daysInRange by segmentSpan.id (I think)
		slice = Math.floor(daysInRange / segmentSpan.id);
	}

	return slice || defaultSlice;
};

export const emailHasExtension = (params) => {
	if (!params || !params.email || !params.extension) {
		return false;
	} else {
		const { email, extension } = params;
		return email.endsWith(extension);
	}
};

// returns an object with begin and end, the most extreme common bounds of the timeseries in the array
export const findCommonRange = (arrayOfTimeseries) => {
	// convert the arrays of objects into arrays of strings (object keys)
	const dateArrays = [];
	_.each(arrayOfTimeseries, (timeseries) => {
		if (timeseries) {
			const keys = Object.keys(timeseries);

			// Make sure the keys are numbers
			const numericalKeys = [];
			_.each(keys, (key) => {
				numericalKeys.push(Number(key));
			});

			dateArrays.push(numericalKeys);
		}
	});

	const beginnings = [];
	const endings = [];

	// make arrays of the first and last dates from each timeseries
	_.each(dateArrays, (keys) => {
		beginnings.push(keys[0]);
		endings.push(keys[keys.length - 1]);
	});

	// Set defaults
	let lowestCommon = beginnings[0];
	let highestCommon = endings[0];

	// find the lowest & highest
	_.each(beginnings, (lowest) => {
		lowestCommon = lowest > lowestCommon ? lowest : lowestCommon;
	});
	_.each(endings, (highest) => {
		highestCommon = highest < highestCommon ? highest : highestCommon;
	});

	return { begin: lowestCommon, end: highestCommon };
};

export const findTourElementPosition = (tour) => {
	// const { } = tour;
	return { top: 200, left: 300 };
};

// returns the passed narrative if it has the parent "narratives"
export const findUsableBaseTrend = (narratives, proposedNarrativeName) => {
	// all single narratives are arrays of one
	let proposedNarrative = narratives[proposedNarrativeName];
	if (!proposedNarrative || proposedNarrative.length < 1) {
		console.error("findUsableBaseTrend - proposedNarrative missing", proposedNarrative);
	}
	if (proposedNarrative && proposedNarrative.length > 0 && proposedNarrative[0].parent !== "narrative") {
		proposedNarrative = _.find(narratives, (nar) => {
			return nar.length === 1 && nar[0].parent === "narratives";
		});
	}

	return proposedNarrative && proposedNarrative.length > 0 && proposedNarrative[0];
};

// Adds colors to narratives & locations, icons to content types, and orders the arrays
export const formatFilters = (filters) => {
	// Add colors to narratives
	const topics = _.find(filters, (filter) => {
		return filter.id === "topics";
	});
	if (topics) {
		const narrativesWithColors = addColorToNestedArray({
			array: topics.children,
			childName: "children",
		});
		topics.children = narrativesWithColors;
	}

	// Add colors to locations
	const locations = _.find(filters, (filter) => {
		return filter.id === "location";
	});
	if (locations) {
		const locationsWithColors = addColorToNestedArray({
			array: locations.children,
			childName: "children",
			altColors: true,
		});
		locations.children = locationsWithColors;
	}

	// Add icons to contentType
	let contentTypes = _.find(filters, (filter) => {
		return filter.id === "type";
	});
	if (contentTypes) {
		contentTypes = addIconsToContentType({
			array: contentTypes,
		});
	}

	// order the list
	// 		order: Narratives, region, contentType, & remainder in the original order
	const region = _.find(filters, (filter) => {
		return filter.id === "location";
	});
	const remainingFilters = _.reject(filters, (filter) => {
		return filter.id === "type" || filter.id === "topics" || filter.id === "location";
	});
	const orderedFilters = [topics, region, contentTypes, ...remainingFilters];

	// Remove any undefined arrays
	const filteredFilters = _.reject(orderedFilters, (filter) => {
		return !filter;
	});

	return filteredFilters;
};

// NOTE: not in use, currently
// Makes sure xAxis lines up with baseTrend
export const formatNewTrend = (newTrend, baseTrend) => {
	// console.log('formatNewTrend', newTrend);
	const adjustedTrend = {
		color: newTrend.color,
		id: newTrend.id,
		name: newTrend.name,
		query: newTrend.query,
		rangeEnd: newTrend.rangeEnd,
		rangeStart: newTrend.rangeStart,
	};
	if (newTrend.rangeStart === baseTrend.rangeStart && newTrend.rangeEnd === baseTrend.rangeEnd) {
		// console.log('trend matches baseTrend');
		adjustedTrend.timeseries = newTrend.timeseries;
	} else if (newTrend.rangeStart <= baseTrend.rangeStart && newTrend.rangeEnd >= baseTrend.rangeEnd) {
		// newTrend fully contains baseTrend - needs a trim
		adjustedTrend.timeseries = _.filter(newTrend.timeseries, (x) => {
			const key = Object.keys(x)[0];
			return key >= baseTrend.rangeStart && key <= baseTrend.rangeEnd;
		});
		console.warn("Trimming timeseries - is this still in order? ...and trimmed correctly?", adjustedTrend.timeseries);
	} else {
		// We have a gap - set a flag so timeseries knows where to start
		adjustedTrend.timeseries = newTrend.timeseries;
		adjustedTrend.misaligned = true;
	}
	return adjustedTrend;
};

// returns an array of objects with {header, bullets} instead of a string. (formatted for InfoTooltip)
export const formatFlatTagsForInfoTooltip = (params) => {
	const { tags, flatNarratives } = params;

	// Find data for all of the tags
	const tagsWithData = _.filter(flatNarratives, (nar) => {
		return _.find(tags, (tagId) => {
			return tagId === nar.id;
		});
	});

	// Format object
	const returnArray = [];
	_.each(tagsWithData, (nar, narId) => {
		returnArray.push({
			header: nar && nar.name,
			color: nar && nar.color,
			id: narId,
		});
	});

	return returnArray;
};

// returns an array of objects with {header, bullets} instead of a string. (formatted for InfoTooltip)
export const formatTagsForInfoTooltip = (params) => {
	const { tags, flatNarratives } = params;

	// Find data for all of the tags
	const tagsWithData = _.filter(flatNarratives, (nar) => {
		return _.find(tags, (tagId) => {
			return tagId === nar.id;
		});
	});

	// divide the tags into narratives & topics
	const narrativeTags = _.reject(tagsWithData, (tag) => {
		return tag.children && tag.children.length > 0;
	});
	const topicTags = _.filter(tagsWithData, (tag) => {
		return tag.children && tag.children.length > 0;
	});

	// Group children by like parents
	const groupedNarrativesObj = _.groupBy(narrativeTags, "parent"); // returns object

	// Include any left over parents
	_.each(topicTags, (tag) => {
		if (!groupedNarrativesObj[tag.id]) {
			// just include the id. No childrent to add as bullets
			groupedNarrativesObj[tag.id] = false;
		}
	});

	// console.log("groupedNarrativesObj", groupedNarrativesObj);
	// Format object
	const returnArray = [];
	_.each(groupedNarrativesObj, (nar, narId) => {
		const parentNarrative = _.find(flatNarratives, (flat) => {
			return flat.id === narId;
		});
		returnArray.push({
			header: parentNarrative && parentNarrative.name,
			color: parentNarrative && parentNarrative.color,
			bullets: nar && _.pluck(nar, "name"),
			id: narId,
		});
	});

	return returnArray;
};

export const getScrollbarSize = () => {
	const doc = document.documentElement;
	const dummyScroller = document.createElement("div");
	dummyScroller.setAttribute("style", "width:99px;height:99px;" + "position:absolute;top:-9999px;overflow:scroll;");
	doc.appendChild(dummyScroller);
	const scrollbarSize = dummyScroller.offsetWidth - dummyScroller.clientWidth;
	doc.removeChild(dummyScroller);
	return scrollbarSize;
};

export const groupNarratives = (options) => {
	const groupHeaders = _.uniq(_.pluck(options, "group"));
	const onlyOneGroup = groupHeaders.length <= 1;
	const groupedByGroup = _.groupBy(options, "group");

	if (onlyOneGroup) {
		return groupedByGroup[groupHeaders[0]] || [];
	} else {
		let headersAndOptions = [];

		_.each(groupHeaders, (header) => {
			headersAndOptions = [...headersAndOptions, { isHeader: true, headerName: header || "other" }, ...groupedByGroup[header]];
		});

		return headersAndOptions;
	}
};

export const highest = (num1, num2) => {
	return num1 > num2 ? num1 : num2;
};

// pair articles (array) with color scheme & formats the tags
// Mutates and returns original array
export const installColorSchemeAndFormatTags = (params) => {
	const { articles, flatNarratives, nested, projectSpecificTagFormat } = params;
	const multiColor = {
		name: "Sky",
		dark: "#accaf2",
		light: "#542792",
	};
	_.each(articles, (art) => {
		// base color
		if (art.narratives && art.narratives.length > 1) {
			art.color = multiColor;
		} else {
			const soloNarrative = _.find(flatNarratives, (trend) => {
				return art.narratives && art.narratives.length > 0 && trend.id === art.narratives[0];
			});
			if (soloNarrative) {
				art.color = soloNarrative.color;
			}
		}

		// tags
		art.tags = projectSpecificTagFormat
			? projectSpecificTagFormat({ tags: art.narratives, flatNarratives })
			: nested
			? formatTagsForInfoTooltip({ tags: art.narratives, flatNarratives })
			: formatFlatTagsForInfoTooltip({ tags: art.narratives, flatNarratives });
	});

	return articles;
};

// Returns a boolean
export const isThisAnEvent = (params) => {
	const { id, events } = params;
	return !!_.find(events, (event) => {
		return event.id === id;
	});
};

// TODO: rewrite this - it's super naieve
// Returns a boolean
export const isUserSubscribed = (params) => {
	const { currentProject, subscriptions } = params;
	let subscribed = false;
	if (currentProject === "gec" || currentProject === "gec_analyst") {
		subscribed = !!_.find(subscriptions, (sub) => {
			return sub === "BAMO-alerts";
		});
	}
	return subscribed;
};

export const log = (text) => {
	if (process.env.NODE_ENV !== "production") {
		console.log("+", ...text);
	}
};

export const lowest = (num1, num2) => {
	return num1 < num2 ? num1 : num2;
};

// Returns a string that contains the narrative.id, perspective, activeFilters & zoom
//		We're using this to validate the most recent API queries
export const makeFetchKey = (narrative, perspective, activeFilters, currentZoom, relevancy) => {
	const date = Date.now();
	const fetchKey = `${(narrative && narrative.id) || "narratives"}${perspective}${_.pluck(activeFilters, "name").join("-")}${
		currentZoom.name || "NoZoom"
	}_${date}_${relevancy || "no_relevancy"}`;
	return fetchKey;
};

// Converts all existing objects with keys into an array
// keys: Array of strings - [key1, key2, ...]
// map: Map object { key1 => any, key2 => any }
export const mapToArray = ({ map, keys }) => {
	let resultsArray = [];

	_.each(keys, (key) => {
		resultsArray = [...resultsArray, ...map[key].values()];
	});

	return resultsArray;
};

// All filters go into one group, separated by OR
//   Returned in () if there are more than one; otherwise just the name returns
export const orFilterGroup = (params) => {
	const { filtersList, altText } = params;

	if (!filtersList || filtersList.length <= 0) {
		return altText !== undefined ? altText : false;
	} else {
		// Make an array of the filter names
		const namesArr = filtersList.map((filter) => {
			const trimmedFilterName = filter.name.trim();
			return removeMd5FromStart({ string: trimmedFilterName });
		});
		// convert the filter names into a string
		const namesString = namesArr.join(" OR ");
		const wrappedString = filtersList.length > 1 ? `(${namesString})` : namesString;

		return wrappedString;
	}
};

// Removes the first instance of an object with a key matching the object's key
// Does this without mutation
export const removeObjectFromArr = (array, object, key) => {
	if (!object) {
		return array;
	} else {
		const index = _.findIndex(array, (arrayObject) => {
			return arrayObject[key] === object[key];
		});
		return array.slice(0, index).concat(array.slice(index + 1));
	}
};

// Does this without mutation
export const removeStringFromArr = (array, string) => {
	const index = _.findIndex(array, (existingString) => {
		return existingString === string;
	});
	return array.slice(0, index).concat(array.slice(index + 1));
};

export const resetPerspective = (perspective) => {
	return perspective === "Content" ? "Source" : perspective === "Reaction" ? "Audience" : perspective;
};

// TODO: Calibrate this for real data
// returns an ordered array of map key objects.
export const setMapKey = (mapData, stops, colors) => {
	const scale = [];
	// Find the biggest value for all years
	const biggestYear = mapData.reduce((year1, year2) => {
		const year1big = year1.data.reduce((state1, state2) => {
			return Number(state1[1]) > Number(state2[1]) ? state1 : state2;
		});
		const year2big = year2.data.reduce((state1, state2) => {
			return Number(state1[1]) > Number(state2[1]) ? state1 : state2;
		});
		return Number(year1big[1]) > Number(year2big[1]) ? year1 : year2;
	});
	const biggestState = biggestYear.data.reduce((state1, state2) => {
		return Number(state1[1]) > Number(state2[1]) ? state1 : state2;
	});
	const biggestValue = Number(biggestState[1]);

	// find a reasonable number for a section
	const keySpan = Math.round(biggestValue / stops / 1000) * 1000;

	for (let i = 0; i < stops; i++) {
		scale.push({
			from: i * keySpan,
			to: i * keySpan + keySpan,
			color: colors[i],
		});
	}
	return scale;
};

// begin: utc date
// end: utc date
// rangeObj: { id: number (of days before end) or "all" }
export const setStartDate = (params) => {
	const { begin, end, rangeObj } = params;
	// returns begin if "all"
	if (!rangeObj || !rangeObj.id || !_.isNumber(rangeObj.id)) {
		return begin;
	} else {
		return Number(moment(Number(end)).subtract(rangeObj.id, "days").format("x"));
	}
};

// returns a tag's short name string
export const tagNameLookup = (params) => {
	const { tag, flatNarratives, hideChildren, hideParents } = params;

	const matchedNarrative = _.find(flatNarratives, (nar) => {
		if (hideParents) {
			return nar.id === tag.id && nar.parent !== "narratives";
		} else if (hideChildren) {
			return nar.id === tag.id && nar.children;
		} else {
			return nar.id === tag.id;
		}
	});

	return matchedNarrative && matchedNarrative.name;
};

export const totalArticlesOnThisDate = (params) => {
	const { aggregationLevel, contentFilteredByDate, trendsToCount } = params;

	if (!contentFilteredByDate) {
		return 0;
	} else {
		let sumOfArticles = 0;
		const dateToCheck = contentFilteredByDate.end - (contentFilteredByDate.end % (24*60*60*1000));

		_.each(trendsToCount, (trend) => {
			const timeseries = trend[aggregationLevel];
			sumOfArticles += timeseries[dateToCheck];
		});

		return sumOfArticles;
	}
};

// React hook helper, to track prevProps in a stateless component
// NOTE: If you're using this to monitor when props update, you can just pass the prop you want to monitor to useEffect instead:
// useEffect(() => {
//   // Do stuff
// }, [propThatYouWantToCheck]);
// For this, add to the component: const prevCount = usePrevious(count)
export const usePrevious = (value) => {
	const ref = useRef();
	useEffect(() => {
		ref.current = value;
	});
	return ref.current;
};

export const writeDateRangeString = ({ 
	aggregationLevel,
	contentFilteredByDate,
	currentZoom,
	adjustForAggregation=false,
}) => {

	// Format date strings for contentFilteredByDate (contentFilteredByDate should already be adjusted for aggregation)
	const filterStartDateObject = contentFilteredByDate && getDateObjectFromUTC(contentFilteredByDate.begin);
	const filterStartDate = filterStartDateObject && `${dateLib.shortMonths[filterStartDateObject.month]} ${filterStartDateObject.day}, ${filterStartDateObject.year}`;
	const filterEndDateObject = contentFilteredByDate && getDateObjectFromUTC(contentFilteredByDate.end);
	const filterEndDate = filterEndDateObject && `${dateLib.shortMonths[filterEndDateObject.month]} ${filterEndDateObject.day}, ${filterEndDateObject.year}`;
	
	// Format date strings for currentZoom
	const zoomStartDateObject = adjustForAggregation 
		? getDateObjectFromUTC(adjustStartDateForAggregation({startDate: currentZoom.begin, aggregationLevel}))
		: getDateObjectFromUTC(currentZoom.begin);
	const zoomStartDate = `${dateLib.shortMonths[zoomStartDateObject.month]} ${zoomStartDateObject.day}, ${zoomStartDateObject.year}`;
	const zoomEndDateObject = getDateObjectFromUTC(currentZoom.end);
	const zoomEndDate = `${dateLib.shortMonths[zoomEndDateObject.month]} ${zoomEndDateObject.day}, ${zoomEndDateObject.year}`;

	// Generate and return the relevant display string
	const dateString = (filterStartDate && filterEndDate)
		? !aggregationLevel || aggregationLevel === "timeseries"
			? `On ${filterStartDate}`
			: `${filterStartDate} to ${filterEndDate}`
		: `${zoomStartDate} to ${zoomEndDate}`;

	return dateString;
};

export const writeDateString = (timestamp) => {
	const dateObject = getDateObjectFromUTC(timestamp);
	return dateObject && `${dateLib.shortMonths[dateObject.month]} ${dateObject.day}, ${dateObject.year}`;
}